import { buildApiUrl } from '../apiConfig';
import { readJsonErrorFromBuffer, saveBinaryDownload } from '../utils/download';

export const AUTH_STORAGE_KEY = 'medadrangi_student_auth';

let unauthorizedHandler = null;

export function setUnauthorizedHandler(handler) {
  unauthorizedHandler = handler;
}

function handleUnauthorized() {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  unauthorizedHandler?.();
}

function getStoredAccessToken() {
  try {
    const storedAuth = localStorage.getItem(AUTH_STORAGE_KEY);

    if (!storedAuth) {
      return null;
    }

    return JSON.parse(storedAuth)?.accessToken || null;
  } catch {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
}

export async function apiRequest(path, options = {}) {
  const { body, headers, ...requestOptions } = options;
  const accessToken = getStoredAccessToken();
  const isAuthEndpoint = path.startsWith('/student/login') || path.startsWith('/student/otp/');
  const skipUnauthorizedHandling = path.startsWith('/student/verify-parent-password');

  const response = await fetch(buildApiUrl(path), {
    ...requestOptions,
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken && !isAuthEndpoint ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const contentType = response.headers.get('content-type') || '';
  const payload = contentType.includes('application/json') ? await response.json() : null;

  if (response.status === 401 && accessToken && !isAuthEndpoint && !skipUnauthorizedHandling) {
    handleUnauthorized();
    throw new Error(payload?.message || 'توکن منقضی شده است.');
  }

  if (!response.ok || payload?.status === false) {
    throw new Error(payload?.message || 'خطایی در ارتباط با سرور رخ داد.');
  }

  return payload;
}

export async function apiFormDataRequest(path, formData, options = {}) {
  const { headers, method = 'POST', ...requestOptions } = options;
  const accessToken = getStoredAccessToken();
  const isAuthEndpoint = path.startsWith('/student/login') || path.startsWith('/student/otp/');

  const response = await fetch(buildApiUrl(path), {
    method,
    ...requestOptions,
    headers: {
      Accept: 'application/json',
      ...(accessToken && !isAuthEndpoint ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    body: formData,
  });

  const contentType = response.headers.get('content-type') || '';
  const payload = contentType.includes('application/json') ? await response.json() : null;

  if (response.status === 401 && accessToken && !isAuthEndpoint) {
    handleUnauthorized();
    throw new Error(payload?.message || 'توکن منقضی شده است.');
  }

  if (!response.ok || payload?.status === false) {
    throw new Error(payload?.message || 'خطایی در ارتباط با سرور رخ داد.');
  }

  return payload;
}

export async function apiDownload(path, options = {}) {
  const { fallbackFilename = 'download.xlsx', headers, ...requestOptions } = options;
  const accessToken = getStoredAccessToken();

  const response = await fetch(buildApiUrl(path), {
    ...requestOptions,
    headers: {
      Accept: '*/*',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
  });

  if (response.status === 401 && accessToken) {
    handleUnauthorized();
    throw new Error('توکن منقضی شده است.');
  }

  const buffer = await response.arrayBuffer();
  const contentType = response.headers.get('content-type') || '';
  const jsonError = readJsonErrorFromBuffer(buffer);

  if (!response.ok || jsonError?.status === false) {
    throw new Error(jsonError?.message || 'دریافت فایل ناموفق بود.');
  }

  saveBinaryDownload(buffer, {
    contentType,
    contentDisposition: response.headers.get('content-disposition') || '',
    fallbackFilename,
  });
}

export async function fetchAuthenticatedFile(fileUrl) {
  const accessToken = getStoredAccessToken();
  const response = await fetch(fileUrl, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });

  if (!response.ok) {
    throw new Error('دریافت فایل انجام نشد.');
  }

  return response.blob();
}

export async function downloadAuthenticatedFile(fileUrl, fileName) {
  const blob = await fetchAuthenticatedFile(fileUrl);
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName || 'file';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
}
